import React, {createContext, PropsWithChildren, useContext} from 'react';
import moment from 'moment';
import {FormikProvider, useFormik} from 'formik';
import {paymentTypeOptions} from '../../_common/constants/options';
import {Option} from '../../_common/types';
import * as actions from '../_redux/bankStatementsActions';
import {useBankStatementsGeneralContext} from "./BankStatementsGeneralContext";
import {MatchedInvoicesRow} from "../types";

type PopupCreateReceiptContext = {
  createReceipt: () => Promise<void>,
  isValid: boolean,
  values: FormikValues,
  resetForm: () => void,
  invoiceID: string,
}

type FormikValues = {
  customerName: string,
  customerID: string,
  customerEmail: string,
  agentID: number,
  currencyID: Option,
  exchangeRate: number,
  comment: string,
  lines: {
    description: string,
    paymentType: Option,
    dueDate: number,
    checkNumber: null,
    bankCode: null,
    branchNumber: null,
    accountNumber: string,
    amount: number | null,
    agentID?: number
  }[],
  invoices: {invoiceID: string, closeAmount: number}[],
}

interface PopupCreateContextProviderProps extends PropsWithChildren<{}> {
  _id: string,
  agentId: number,
  amount: number,
  customerEmail: string,
  customerID: string,
  customerName: string,
  status: number
  tableRowID: number
}

const PopupCreateReceiptContext = createContext<PopupCreateReceiptContext>({
  createReceipt: async () => {},
  isValid: false,
  values: {} as FormikValues,
  resetForm: () => {},
  invoiceID: ''
});

export function usePopupCreateReceiptContext() {
  return useContext(PopupCreateReceiptContext);
}


export function PopupCreateReceiptProvider({
                                             _id,
                                             agentId,
                                             amount,
                                             customerEmail,
                                             customerID,
                                             customerName,
                                             tableRowID,
                                             children
                                           }: PopupCreateContextProviderProps) {
  const {tableRows, setTableRows} = useBankStatementsGeneralContext();

  const initialValues = {
    customerName: customerName || '',
    customerID: customerID || '',
    customerEmail: customerEmail || '',
    agentID: agentId,
    currencyID: {value: 1, label: 'ש"ח'},
    exchangeRate: 1,
    comment: '',
    lines: [{
      description: '',
      paymentType: paymentTypeOptions.filter(item => item.value === 9)[0],
      dueDate: Date.parse(moment().format('YYYY-MM-DD')),
      checkNumber: null,
      bankCode: null,
      branchNumber: null,
      accountNumber: '',
      amount: amount || null,
    }],
    invoices: _id ? [{invoiceID: _id, closeAmount: amount}] : [],
  };

  const preparedData = (values: FormikValues) => {
    const {
      customerID,
      customerEmail,
      agentID,
      currencyID,
      exchangeRate,
      comment,
      lines,
      invoices
    } = values;

    const linesData = Array.isArray(lines)
      ? lines.map((item) => {
        return {
          description: item.description || '',
          paymentType: item.paymentType ? parseInt(item.paymentType.value.toString()) : null,
          dueDate: item.dueDate
            ? Date.parse(moment(item.dueDate).format('YYYY-MM-DD'))
            : Date.parse(moment().format('YYYY-MM-DD')),
          checkNumber: item.checkNumber ? parseInt(item.checkNumber) : null,
          bankCode: item.bankCode ? parseInt(item.bankCode) : null,
          branchNumber: item.branchNumber ? parseInt(item.branchNumber) : null,
          accountNumber: item.accountNumber || '',
          amount: item.amount ? parseFloat(item.amount.toString()) : null,
        };
      })
      : [];

    return {
      invoiceID: _id,
      multipleAgents: 0,
      agentID,
      customerID,
      customerEmail,
      currencyID: currencyID.value ? parseInt(currencyID.value.toString(), 10) : null,
      exchangeRate: parseInt(exchangeRate.toString(), 10) || null,
      comment: comment || '',
      lines: linesData,
      invoices,
    };
  };

  const validate = (values: FormikValues) => {
    type Errors = Partial<{
      accountNumber: string;
      amount: string;
      bankCode: string;
      branchNumber: string;
      checkNumber: string;
      paymentType: string;
    }> & Partial<{
      [p in keyof FormikValues]: string
    }>
    let errors: Errors = {};
    const {currencyID, exchangeRate, lines} = values;

    if (!currencyID) {
      errors['currencyID'] = 'Required';
    }

    if (!exchangeRate) {
      errors['exchangeRate'] = 'Required';
    }

    if (lines.length === 0) {
      errors.lines = 'Required at least one line';
    }

    if (Array.isArray(lines)) {
      lines.map((item) => {
        return {
          agentID: item.agentID,
          accountNumber: item.accountNumber,
          amount: item.amount,
          bankCode: item.bankCode,
          branchNumber: item.branchNumber,
          checkNumber: item.checkNumber,
          paymentType: item.paymentType,
        };
      })
        .forEach((item) => {
          const {
            accountNumber,
            amount,
            bankCode,
            branchNumber,
            checkNumber,
            paymentType
          } = item;
          if (!accountNumber && (paymentType.value === 1)) errors['accountNumber'] = 'Required line item';
          if (!amount) errors['amount'] = 'Required line item';
          if (!bankCode && (paymentType.value === 1)) errors['bankCode'] = 'Required line item';
          if (!branchNumber && (paymentType.value === 1)) errors['branchNumber'] = 'Required line item';
          if (!checkNumber && (paymentType.value === 1)) errors['checkNumber'] = 'Required line item';
          if (!paymentType) errors['paymentType'] = 'Required line item';
        });
    }

    return errors;
  };

  const formik = useFormik<FormikValues>({
    initialValues,
    onSubmit: () => {},
    validate,
    validateOnMount: true,
    validateOnChange: true,
  });

  const getIndex = (array: MatchedInvoicesRow[], tableRowID: number) => {
    const mappedArray = array.map(item => item.id);
    return mappedArray.indexOf(tableRowID);
  }

  const markInvoiceAsUsed = () => {
    const neededIndex = getIndex(tableRows, tableRowID);

    const updatedTableRows = tableRows.map((item, index) => {
      if (index === neededIndex) {
        return { ...item, isUsed: true };
      }
      return item;
    });

    setTableRows(updatedTableRows);
  };

  const createReceipt = async () => {
    const data = preparedData(formik.values);
    const receiptID = await actions.createReceipt(data);
    if (receiptID) {
      markInvoiceAsUsed();
    }
  };

  return (
    <PopupCreateReceiptContext.Provider value={{
      createReceipt,
      isValid: formik.isValid,
      values: formik.values,
      resetForm: formik.resetForm,
      invoiceID: _id,
    }}>
      <FormikProvider value={formik}>
        {children}
      </FormikProvider>
    </PopupCreateReceiptContext.Provider>
  );
}
